// "Sign in with itch.io": OAuth implicit flow + the cloud profile that makes it worth doing.
//
// itch.io hands browser games NO automatic identity (a long-standing platform gap), so the player
// authorizes once in a popup. The token returns through OUR redirect page (the registered
// redirect_uri on pages.dev) via postMessage, because the game itself runs on itch's sandboxed CDN
// origin and can never BE a redirect target. The itch API sends no CORS headers either, so the MP
// worker (party/index.ts) resolves the token server-side and keys the cloud profile by itch USER ID.
//
// Progress transfer: on login the local (guest) profile and the cloud profile MERGE, idempotently -
// unions and per-field maxima, never sums - so repeated merges can never double-pay anything. The
// merged result becomes both sides' truth, and afterwards every local profile write pushes up,
// debounced, via the storage layer's persist listener.

import { PARTY_HOST } from '../net/protocol';
import { loadProfile, importProfile, onProfilePersist, type Profile, type RaceStat } from './storage';

const ITCH_CLIENT_ID = '88df864ade074e5f15a67a6578fa9b17';
// MUST match the OAuth app's registered redirect_uri exactly. The workers.dev mirror, NOT pages.dev:
// pages.dev is DNS-blocked by some ISPs (measured unreachable from the dev's own network), and a
// redirect host that half the audience cannot resolve would break sign-in for exactly those players.
const AUTH_PAGE = 'https://warring-isles.ardauyaroglu.workers.dev/itch-auth.html';
const AUTH_KEY = 'wi.itchAuth';

export interface ItchIdentity {
  token: string;
  id: number;
  username: string;
  displayName: string;
  avatarUrl: string | null;
}

const workerBase = (): string => {
  const secure = PARTY_HOST.includes('workers.dev') || PARTY_HOST.startsWith('https');
  return `${secure ? 'https' : 'http'}://${PARTY_HOST.replace(/^https?:\/\//, '')}`;
};

export function itchIdentity(): ItchIdentity | null {
  try {
    const raw = localStorage.getItem(AUTH_KEY);
    if (!raw) return null;
    const p = JSON.parse(raw) as ItchIdentity;
    return p && typeof p === 'object' && typeof p.token === 'string' && p.token ? p : null;
  } catch {
    return null;
  }
}

function storeIdentity(idn: ItchIdentity | null): void {
  try {
    if (idn) localStorage.setItem(AUTH_KEY, JSON.stringify(idn));
    else localStorage.removeItem(AUTH_KEY);
  } catch {
    /* storage best-effort */
  }
}

export function itchLogout(): void {
  storeIdentity(null);
  onProfilePersist(null); // stop pushing; local play continues untouched
}

/** One popup round-trip: itch authorize -> our redirect page -> postMessage token -> worker /me.
 *  Resolves null when the popup was blocked, closed, or the token failed to verify. */
export function itchLogin(): Promise<ItchIdentity | null> {
  const url = `https://itch.io/user/oauth?client_id=${ITCH_CLIENT_ID}&scope=${encodeURIComponent('profile:me')}&response_type=token&redirect_uri=${encodeURIComponent(AUTH_PAGE)}`;
  const popup = window.open(url, 'wi-itch-login', 'width=560,height=720');
  if (!popup) return Promise.resolve(null); // popup blocked: the caller shows the allow-popups hint
  return new Promise((resolve) => {
    let settled = false;
    let watch = 0;
    const finish = (v: ItchIdentity | null): void => {
      if (settled) return;
      settled = true;
      window.removeEventListener('message', onMsg);
      window.clearInterval(watch);
      resolve(v);
    };
    const onMsg = (e: MessageEvent): void => {
      if (e.origin !== new URL(AUTH_PAGE).origin) return; // only OUR redirect page may hand us a token
      const d = e.data as { type?: string; token?: string } | null;
      if (!d || d.type !== 'wi-itch-auth' || !d.token) return;
      const token = d.token;
      void fetch(`${workerBase()}/itch/me`, { headers: { authorization: `Bearer ${token}` } })
        .then((r) => (r.ok ? (r.json() as Promise<{ user?: Omit<ItchIdentity, 'token'> }>) : null))
        .then((j) => {
          if (!j?.user) return finish(null);
          const idn: ItchIdentity = { token, ...j.user };
          storeIdentity(idn);
          finish(idn);
        })
        .catch(() => finish(null));
    };
    window.addEventListener('message', onMsg);
    // the popup closing without a message = the player backed out (grace period: close can race the message)
    watch = window.setInterval(() => {
      if (popup.closed) window.setTimeout(() => finish(null), 600);
    }, 500);
  });
}

// --- the idempotent profile merge ---------------------------------------------------------------

const nmax = (a: unknown, b: unknown): number => Math.max(typeof a === 'number' && a > 0 ? a : 0, typeof b === 'number' && b > 0 ? b : 0);
const amax = (a: readonly number[] = [], b: readonly number[] = []): number[] => Array.from({ length: Math.max(a.length, b.length) }, (_, i) => nmax(a[i], b[i]));
const union = (a: readonly string[] = [], b: readonly string[] = []): string[] => [...new Set([...a, ...b])];

/** Merge two profiles so that replaying the merge never changes the result again (unions + maxima,
 *  never sums): cross-device logins and re-logins stay safe. LOCAL wins the taste calls (equipped
 *  cosmetics, guest name); records take the better of the two sides. */
export function mergeProfiles(local: Profile, remote: Partial<Profile>): Profile {
  const races = union(Object.keys(local.byRace), Object.keys(remote.byRace ?? {}));
  const byRace: Record<string, RaceStat> = {};
  for (const r of races) {
    const a = local.byRace[r];
    const b = remote.byRace?.[r];
    const bests = [a?.bestRound, b?.bestRound].filter((x): x is number => typeof x === 'number' && x > 0);
    byRace[r] = { games: nmax(a?.games, b?.games), wins: nmax(a?.wins, b?.wins), bestRound: bests.length ? Math.min(...bests) : null };
  }
  const starIds = union(Object.keys(local.campaignStarsById), Object.keys(remote.campaignStarsById ?? {}));
  const campaignStarsById: Record<string, number> = {};
  for (const id of starIds) campaignStarsById[id] = Math.min(3, nmax(local.campaignStarsById[id], remote.campaignStarsById?.[id]));
  // the daily streak belongs to whichever side claimed a reward more recently (YYYY-MM-DD compares as text)
  const localDailyNewer = (local.lastDaily || '') >= (remote.lastDaily ?? '');
  return {
    games: nmax(local.games, remote.games),
    wins: nmax(local.wins, remote.wins),
    byRace,
    tilesCaptured: nmax(local.tilesCaptured, remote.tilesCaptured),
    kills: amax(local.kills, remote.kills),
    built: amax(local.built, remote.built),
    elmas: nmax(local.elmas, remote.elmas),
    unlocks: {
      avatars: union(local.unlocks.avatars, remote.unlocks?.avatars),
      frames: union(local.unlocks.frames, remote.unlocks?.frames),
      sets: union(local.unlocks.sets, remote.unlocks?.sets),
    },
    equipped: local.equipped,
    guestName: local.guestName,
    tutorialDone: local.tutorialDone || !!remote.tutorialDone,
    tutorialChapters: union(local.tutorialChapters, remote.tutorialChapters),
    campaignGuided: union(local.campaignGuided, remote.campaignGuided),
    itemsBought: nmax(local.itemsBought, remote.itemsBought),
    mpGames: nmax(local.mpGames, remote.mpGames),
    mpWins: nmax(local.mpWins, remote.mpWins),
    mpKills: nmax(local.mpKills, remote.mpKills),
    campaignStarsById,
    campaignStars: [], // derived: importProfile's hydrate rebuilds it from campaignStarsById
    lastDaily: localDailyNewer ? local.lastDaily : (remote.lastDaily ?? ''),
    dailyStreak: localDailyNewer ? local.dailyStreak : nmax(remote.dailyStreak, 0),
  };
}

// --- cloud pull/push ----------------------------------------------------------------------------

async function pullCloud(idn: ItchIdentity): Promise<Partial<Profile> | null | 'unauthorized'> {
  const res = await fetch(`${workerBase()}/itch/profile`, { headers: { authorization: `Bearer ${idn.token}` } });
  if (res.status === 401) return 'unauthorized';
  if (!res.ok) throw new Error(`profile pull ${res.status}`);
  const j = (await res.json()) as { profile?: Partial<Profile> | null };
  return j.profile ?? null;
}

async function pushCloud(idn: ItchIdentity, prof: Profile): Promise<void> {
  await fetch(`${workerBase()}/itch/profile`, {
    method: 'PUT',
    headers: { authorization: `Bearer ${idn.token}`, 'content-type': 'application/json' },
    body: JSON.stringify(prof),
  });
}

let pushTimer = 0;
function schedulePush(): void {
  window.clearTimeout(pushTimer);
  pushTimer = window.setTimeout(() => {
    const idn = itchIdentity();
    if (idn) void pushCloud(idn, loadProfile()).catch(() => void 0); // best-effort; the next write retries
  }, 3000);
}

/** Pull -> merge -> apply locally -> push. The login-time "transfer": after this the itch account owns
 *  everything the guest earned. Also the boot-time silent refresh for an already-linked device. */
export async function itchSyncProfile(idn: ItchIdentity): Promise<Profile | null> {
  try {
    const remote = await pullCloud(idn);
    if (remote === 'unauthorized') {
      itchLogout(); // token revoked/expired: back to guest until the next sign-in
      return null;
    }
    const merged = mergeProfiles(loadProfile(), remote ?? {});
    const applied = importProfile(merged);
    onProfilePersist(schedulePush); // AFTER the import, so the import itself does not schedule a loop
    await pushCloud(idn, applied);
    return applied;
  } catch {
    return null; // offline / worker down: local play continues, next boot retries
  }
}

/** Boot hook: if this device is already linked, quietly refresh the cloud copy and arm the push hook. */
export function initItchCloud(): void {
  const idn = itchIdentity();
  if (!idn) return;
  onProfilePersist(schedulePush);
  void itchSyncProfile(idn);
}
